import NextLink from 'next/link'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'

import structure from '@/structure'
import { colors } from '@/styles'
import { H4, Label, ParagraphRegular } from '@/components/text'

const Wrapper = styled.section`
  background-color: ${colors.background};
  display: flex;
  justify-content: flex-start;
  max-width: ${structure.page.width}px;
  width: 100%;
  margin-bottom: 64px;
`

const InnerWrapper = styled.div`
  max-width: ${structure.page.width}px;
  width: calc(100% - 40px);
  margin: 0;
  display: block;
`

const SectionTitle = styled(H4)`
  color: ${colors.regular};
  margin: 0 0 32px;
`

const LinkWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 32px;

  @media only screen and (max-width: 1099px) {
    flex-direction: column;
    align-items: start;
  }
`

const LinkBlock = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;

  @media only screen and (max-width: 1099px) {
    width: 100%;
  }
`

const Title = styled(ParagraphRegular)`
  color: ${colors.grey16};
  font-weight: 600;
  margin: 0 0 20px;
`

const Link = styled.a`
  display: inline-block;
  text-decoration: none;
  color: ${colors.blue40};
  transition: opacity 0.1s ease-in-out;
  margin-bottom: 16px;

  div {
    font-weight: 400;
  }

  &:hover {
    opacity: 0.5;
  }
`

const CityLink = ({ href, text }: { href: string; text: string }) => {
  return (
    <NextLink href={href} passHref legacyBehavior>
      <Link>
        <Label fontSize="14px">{text}</Label>
      </Link>
    </NextLink>
  )
}

const CityLinkList = () => {
  const { t } = useTranslation()
  return (
    <Wrapper>
      <InnerWrapper>
        <SectionTitle as="h2">
          {t(
            'listing:b.city_link_list.view.homes_and_apartments.title',
            'Homes and Apartments for Rent in Popular Cities'
          )}
        </SectionTitle>
        <LinkWrapper>
          <LinkBlock>
            <Title as="h3">
              {t(
                'listing:b.city_link_list.view.bc_homes_and_apartments.title',
                'British Columbia Homes and Apartments for Rent'
              )}
            </Title>
            <CityLink
              href="/rental-listings/city/vancouver"
              text={t(
                'listing:b.city_link_list.view.vancouver_apartments.link',
                'Vancouver Apartments for Rent'
              )}
            />
            <CityLink
              href="/rental-listings/city/burnaby"
              text={t(
                'listing:b.city_link_list.view.burnaby_apartments.link',
                'Burnaby Apartments for Rent'
              )}
            />
            <CityLink
              href="/rental-listings/city/north-vancouver"
              text={t(
                'listing:b.city_link_list.view.north_vancouver_apartments.link',
                'North Vancouver Apartments for Rent'
              )}
            />
            <CityLink
              href="/rental-listings/city/richmond"
              text={t(
                'listing:b.city_link_list.view.richmond_apartments.link',
                'Richmond Apartments for Rent'
              )}
            />
            <CityLink
              href="/rental-listings/city/surrey"
              text={t(
                'listing:b.city_link_list.view.surrey_apartments.link',
                'Surrey Apartments for Rent'
              )}
            />
            <CityLink
              href="/rental-listings/city/coquitlam"
              text={t(
                'listing:b.city_link_list.view.coquitlam_apartments.link',
                'Coquitlam Apartments for Rent'
              )}
            />
          </LinkBlock>
          <LinkBlock>
            <Title as="h3">
              {t(
                'listing:b.city_link_list.view.on_homes_and_apartments.title',
                'Ontario Homes and Apartments for Rent'
              )}
            </Title>
            <CityLink
              href="/rental-listings/city/toronto"
              text={t(
                'listing:b.city_link_list.view.toronto_apartments.link',
                'Toronto Apartments for Rent'
              )}
            />
            <CityLink
              href="/rental-listings/city/mississauga"
              text={t(
                'listing:b.city_link_list.view.mississauga_apartments.link',
                'Mississauga Apartments for Rent'
              )}
            />
            <CityLink
              href="/rental-listings/city/scarborough"
              text={t(
                'listing:b.city_link_list.view.scarborough_apartments.link',
                'Scarborough Apartments for Rent'
              )}
            />
            <CityLink
              href="/rental-listings/city/etobicoke"
              text={t(
                'listing:b.city_link_list.view.etobicoke_apartments.link',
                'Etobicoke Apartments for Rent'
              )}
            />
            <CityLink
              href="/rental-listings/city/hamilton"
              text={t(
                'listing:b.city_link_list.view.hamilton_apartments.link',
                'Hamilton Apartments for Rent'
              )}
            />
            <CityLink
              href="/rental-listings/city/brampton"
              text={t(
                'listing:b.city_link_list.view.brampton_apartments.link',
                'Brampton Apartments for Rent'
              )}
            />
          </LinkBlock>
        </LinkWrapper>
      </InnerWrapper>
    </Wrapper>
  )
}

export default CityLinkList
